<template>
  <div class="box city-fixed">
    <div class="box-title flex items-center justify-between">
      <el-button @click="saveVersion" type="success" size="mini">
        Save Data
      </el-button>
      <div class="flex items-center gap-1" v-if="directoryPlan">
        <el-button type="default" size="mini" @click="sampleExcel">
          Sample Excel
        </el-button>
        <el-upload
          :action="`/api/v1/base_rates/import?hub_id=${hub.id}&category=city_fixed`"
          accept=".csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          :show-file-list="false"
          :on-success="handleUploadSuccess"
          :on-error="handleUploadError"
        >
          <el-button type="default" size="mini">Import Excel</el-button>
        </el-upload>
      </div>
      <div class="flex items-center" v-if="!directoryPlan">
        <div class="select-prepend">Mode</div>
        <el-select v-model="baseRate.mode" size="mini">
          <el-option label="Weekly FSC" value="weekly_fsc" />
          <el-option label="Static" value="static" />
        </el-select>
      </div>
    </div>
    <div class="box-content">
      <CityFixedWeeklyFsc
        v-if="baseRate.mode == 'weekly_fsc'"
        :key="version"
        :hub="hub"
        :baseRate="baseRate"
        :options="options"
      />
      <CityFixedStatic
        v-if="baseRate.mode == 'static'"
        :key="version"
        :hub="hub"
        :baseRate="baseRate"
        :options="options"
      />
    </div>
  </div>
</template>
<script>
import CityFixedWeeklyFsc from "@/default/pages/base_rates/modes/city_fixed/weekly_fsc.vue";
import CityFixedStatic from "@/default/pages/base_rates/modes/city_fixed/static.vue";
export default {
  name: "CityFixed",
  components: { CityFixedWeeklyFsc, CityFixedStatic },
  props: {
    hub: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      baseRate: {},
      cachedBaseRate: {},
      version: 0,
      options: {
        terminals: [],
        customers: [],
        warehouses: [],
        yards: [],
        depots: [],
        categories: [],
        driverTypes: {
          cd: "Company",
          oo: "Owner Operator",
          bk: "Broker",
        },
      },
      notification: null,
      loading: null,
    };
  },
  async mounted() {
    await this.loadOptions();
    await this.loadData();
  },
  watch: {
    isChanged(nv, ov) {
      if (nv) {
        this.notification = this.$notify({
          title: `${this.hub.name}`,
          message: "Changes aren't saved until you click the 'Save Data'.",
          type: "warning",
          duration: 0,
          showClose: false,
          customClass: "dense",
        });
      } else {
        if (this.notification) {
          this.notification.close();
          this.notification = null;
        }
      }
    },
  },
  computed: {
    isChanged() {
      return (
        JSON.stringify(this.baseRate) !== JSON.stringify(this.cachedBaseRate)
      );
    },
  },
  methods: {
    loadingService() {
      return this.$loading.service({
        target: ".base-rates",
        lock: true,
        text: "Please wait while saving data",
        spinner: "el-icon-loading",
        background: "rgba(255, 255, 255, 0.8)",
      });
    },
    async loadOptions() {
      try {
        let response = await this.$http.get("/api/v1/options.json", {
          params: {
            targets: ["depots", "terminals", "customers", "yards", "places"],
            active: true,
            q: { hub_id_eq: this.hub.id },
          },
        });
        this.$set(this.options, "depots", response.data.depots);
        this.$set(this.options, "terminals", response.data.terminals);
        this.$set(this.options, "customers", response.data.customers);
        this.$set(this.options, "yards", response.data.yards);
        this.$set(this.options, "places", response.data.places);

        response = await this.$http.get("/api/v1/options.json", {
          params: {
            target: "warehouses",
            active: true,
            scope: "all",
          },
        });
        this.$set(this.options, "warehouses", response.data);

        response = await this.$http.get("/api/v1/categories/accounting.json", {
          params: { scope: "all", mode: "details" },
        });
        this.$set(this.options, "categories", response.data);
      } catch (ex) {}
    },
    async loadData() {
      try {
        const response = await this.$http.get("/api/v1/base_rates/hub", {
          params: { hub_id: this.hub.id, mode: this.hub.quote_mode },
        });
        if (response.data[0]) {
          this.baseRate = response.data[0].data;
          this.baseRate.id = response.data[0].id;
          this.cachedBaseRate = JSON.parse(JSON.stringify(this.baseRate));
          this.version++;
        }
      } catch (ex) {
        console.log(ex);
      }
    },
    async saveVersion() {
      try {
        this.loading = this.loadingService();
        // Create file from entire baseRate data
        const blob = new Blob([JSON.stringify(this.baseRate)], {
          type: "application/json",
        });
        const file = new File([blob], "base_rate_data.json", {
          type: "application/json",
        });

        await this.$http.uploadPost(
          "/api/v1/base_rates/build.json",
          "file",
          file,
          {
            id: this.baseRate.id,
            hub_id: this.hub.id,
            category: "city_fixed",
          },
        );

        // Reload data to get the latest state
        await this.loadData();

        this.$message({
          message: "The base rate has been saved.",
          type: "success",
        });
      } catch (ex) {
        this.$message({ message: ex.response.data.errors, type: "error" });
      } finally {
        this.loading.close();
      }
    },
    sampleExcel() {
      this.openUrl(`/api/v1/base_rates/template.xlsx?hub_id=${this.hub.id}`);
    },
    async handleUploadSuccess(res, file) {
      await this.loadData();
      this.$message({
        message: "The excel has been imported.",
        type: "success",
      });
    },
    handleUploadError(err, file, fileList) {
      const message = JSON.parse(err.message);
      this.$message({ message: message.errors, type: "error", duration: 5000 });
    },
  },
};
</script>
<style lang="scss" scoped>
.city-fixed::v-deep {
  @apply h-full flex flex-col;
  .el-input-group__prepend,
  .el-input-group__append {
    @apply px-1 bg-slate-100 border-gray-400 #{!important};
  }
  .box-content {
    @apply grow overflow-auto #{!important};
  }
  .select-prepend {
    @apply border border-r-0 rounded-l px-1 py-0.5 h-7 text-gray-600 bg-slate-100 border-gray-400;
  }
}
</style>
